import React, { useRef, useState } from 'react';
import { Box, Card, Stack, Typography, IconButton, Button, Chip, useMediaQuery } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { IAudition } from '../../types/global/helper';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { TextWithIcon } from '../TextWithIcon/TextWithIcon';
import GpsFixedOutlined from '@mui/icons-material/GpsFixedOutlined';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { RectangularImageCropperModal } from '../ImageCropper/RectangularImageCropperModal';
import { RICH_MEDIA_SHARE_ASPECT } from '../../constants/richMediaShareImage';
import { RichMediaShareSizeHint } from '../Common/RichMediaShareSizeHint';
import moment from 'moment';
import { AUDITION_FIELD_ACCENT } from './auditionFieldTheme';
import { EsBackButton } from '../EsButtons/EsBackButton';

export interface ICoverImageRenderer {
    audition: IAudition;
    editMode?: boolean;
    onImageChange?: (image: string, rawFile: File) => void;
    /** Full-bleed hero with bottom gradient + bento-style title (casting detail layout). */
    immersive?: boolean;
    /** Image + edit controls only — no title, tags, location, or chips on the image (e.g. brand dashboard). */
    minimalOverlays?: boolean;
    onBackButtonPressed?: () => void;
}

function TitleWithAccent({ title }: { title: string }) {
    const parts = title.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
        return (
            <Box component="span" sx={{ color: AUDITION_FIELD_ACCENT }}>
                {parts[0] ?? title}
            </Box>
        );
    }
    const last = parts.pop()!;
    const rest = parts.join(' ');
    return (
        <>
            {rest}{' '}
            <Box component="span" sx={{ color: AUDITION_FIELD_ACCENT }}>
                {last}
            </Box>
        </>
    );
}

const CoverImageRenderer: React.FC<ICoverImageRenderer> = ({
    audition,
    editMode = false,
    onImageChange,
    immersive = false,
    minimalOverlays = false,
    onBackButtonPressed,
}) => {
    const { themeStyle } = useThemeContext();
    const isShortViewport = useMediaQuery('(max-height: 720px)');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [src, setSrc] = useState<string>('');
    const [pendingImage, setPendingImage] = useState<string | null>(null);
    const [pendingFile, setPendingFile] = useState<File | null>(null);

    const useImmersive = immersive && !editMode;

    const handleUploadClick = () => {
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            setSrc(URL.createObjectURL(file));
            setModalOpen(true);
        }
    };

    const handleCropSave = (base64: string, file: File) => {
        setModalOpen(false);
        setSrc('');
        setPendingImage(base64);
        setPendingFile(file);
    };

    const handleCropCancel = () => {
        setModalOpen(false);
        setSrc('');
    };

    const handlePendingCancel = () => {
        setPendingImage(null);
        setPendingFile(null);
    };

    const handlePendingSave = () => {
        if (pendingImage && pendingFile && onImageChange) {
            onImageChange(pendingImage, pendingFile);
        }
        setPendingImage(null);
        setPendingFile(null);
    };

    const displayImage = pendingImage || audition?.imageCover;

    const isCastingClosed = !!audition.casting_expiration && moment(audition.casting_expiration).isBefore(moment());

    /** Bottom strip matches page `pure_black` so the hero meets content without a seam; do not shift layout. */
    const pageBase = theme[themeStyle].pure_black;
    /** Layered gradients: readable title + tags; `center top` keeps portraits from feeling overly cropped. */
    const immersiveBgImage = displayImage
        ? {
              xs: `linear-gradient(to bottom, transparent 0%, transparent calc(100% - 40px), ${pageBase} 100%), linear-gradient(to top, rgba(14, 14, 14, 0.95) 0%, rgba(14, 14, 14, 0.5) 42%, transparent 72%), linear-gradient(to top, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.2)), url(${displayImage})`,
              sm: `linear-gradient(to bottom, transparent 0%, transparent calc(100% - 40px), ${pageBase} 100%), linear-gradient(to top, rgba(14, 14, 14, 0.95) 0%, rgba(14, 14, 14, 0.5) 42%, transparent 72%), linear-gradient(to right, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.2)), url(${displayImage})`,
          }
        : undefined;

    const immersiveBg = displayImage ? immersiveBgImage : audition.background_color;

    const classicBg = displayImage
        ? `linear-gradient(to top, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.3)), url(${displayImage})`
        : audition.background_color;

    const immersiveMinHeight = isShortViewport ? { xs: 200, sm: 240, md: 260 } : { xs: 280, sm: 360, md: 420 };

    const outerSx = useImmersive
        ? {
              width: '100%',
              maxWidth: '100%',
              alignSelf: 'stretch',
              borderRadius: 0,
              overflow: 'hidden',
              position: 'relative' as const,
              my: 0,
              minHeight: immersiveMinHeight,
              backgroundColor: displayImage ? 'transparent' : audition.background_color,
          }
        : {
              width: '100%',
              borderRadius: 2,
              overflow: 'hidden',
              objectFit: 'cover' as const,
              position: 'relative' as const,
              my: 2,
              height: { xs: '300px' },
              backgroundColor: displayImage ? 'transparent' : audition.background_color,
          };

    const innerSx = {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-start',
        position: 'relative' as const,
        ...(useImmersive && displayImage
            ? {
                  backgroundImage: immersiveBgImage,
                  backgroundColor: undefined,
              }
            : { background: useImmersive ? immersiveBg : classicBg }),
        backgroundSize: 'cover',
        backgroundPosition: useImmersive ? { xs: 'center top', sm: 'center center' } : 'center',
        backgroundRepeat: 'no-repeat',
        height: '100%',
        width: '100%',
        minHeight: useImmersive ? immersiveMinHeight : undefined,
        overflow: 'hidden',
    };

    const inner = (
        <Box id="featured-contest" sx={innerSx}>
            {editMode && !pendingImage && (
                <>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: 15,
                            right: 15,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: theme[themeStyle].primary_white,
                            zIndex: 10,
                            '&:hover': { backgroundColor: theme[themeStyle].primary_orange },
                        }}
                        onClick={handleUploadClick}
                        aria-label="Upload Cover Image">
                        <PhotoCamera sx={{ color: theme[themeStyle].true_white }} />
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                    </IconButton>
                    <RichMediaShareSizeHint
                        sx={{
                            position: 'absolute',
                            bottom: 15,
                            right: 64,
                            zIndex: 10,
                            color: theme[themeStyle].primary_white,
                            opacity: 0.75,
                            fontSize: 10,
                            mt: 0,
                            textAlign: 'right',
                            maxWidth: 140,
                            lineHeight: 1.3,
                        }}
                    />
                </>
            )}
            <RectangularImageCropperModal
                open={modalOpen}
                src={src}
                onSave={handleCropSave}
                onCancel={handleCropCancel}
                aspect={RICH_MEDIA_SHARE_ASPECT}
                cropHeight={300}
                variant="richMediaShare"
            />
            {editMode && pendingImage && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 20,
                        background: 'rgba(0,0,0,0.25)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                    <img
                        src={pendingImage}
                        alt="Preview"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 8,
                            boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
                        }}
                    />
                    <Box sx={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 2 }}>
                        <Button variant="outlined" color="inherit" onClick={handlePendingCancel} sx={{ mr: 1 }}>
                            Cancel
                        </Button>
                        <Button variant="contained" color="primary" onClick={handlePendingSave}>
                            Save
                        </Button>
                    </Box>
                </Box>
            )}
            {!minimalOverlays && (
                <>
                    {onBackButtonPressed && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                p: { xs: 2, sm: 2.5, md: 3 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                zIndex: 3,
                            }}>
                            <EsBackButton handleDetails={onBackButtonPressed} />
                        </Box>
                    )}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 15,
                            top: onBackButtonPressed ? { xs: 52, sm: 56 } : 15,
                            zIndex: 2,
                        }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {!useImmersive && audition.union_affiliation && (
                                <Chip
                                    label={audition.union_affiliation}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#000',
                                        color: theme[themeStyle].true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        textTransform: 'uppercase',
                                        fontSize: 10,
                                        height: '24px',
                                        borderRadius: '6px',
                                    }}
                                />
                            )}
                            <CustomChipGroup tags={audition.tags} clickable={false} withNiceColor />
                        </Stack>
                    </Box>
                    <Box sx={{ position: 'absolute', right: 15, top: 15, zIndex: 2 }}>
                        <CustomChipGroup tags={[audition.gig_type]} clickable={false} withNiceColor />
                    </Box>
                </>
            )}

            {!minimalOverlays &&
                (useImmersive ? (
                    <Stack
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        p={{ xs: 2, sm: 3, md: 4 }}
                        spacing={1.5}
                        sx={{
                            zIndex: 2,
                            minWidth: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                        }}>
                        <Chip
                            label={isCastingClosed ? 'Casting closed' : 'Active casting call'}
                            sx={{
                                alignSelf: 'flex-start',
                                bgcolor: 'rgba(216, 146, 34, 0.18)',
                                color: AUDITION_FIELD_ACCENT,
                                border: '1px solid rgba(255, 156, 40, 0.35)',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                fontSize: 10,
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                                height: 28,
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                                lineHeight: 1.15,
                                color: theme[themeStyle].true_white,
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                maxWidth: '100%',
                            }}>
                            <TitleWithAccent title={audition.title} />
                        </Typography>
                        <TextWithIcon
                            icon={<GpsFixedOutlined sx={{ fontSize: 14, color: theme[themeStyle].true_white }} />}
                            text={audition?.shoot_location?.address ? audition.shoot_location.address : 'Remote'}
                            typographyProps={{
                                sx: {
                                    fontSize: 12,
                                    color: 'rgba(255,255,255,0.85)',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                },
                            }}
                        />
                        {audition.required_media && audition.required_media.length > 0 && (
                            <CustomChipGroup
                                tags={audition.required_media}
                                tagSx={{
                                    color: theme[themeStyle].true_white,
                                }}
                            />
                        )}
                    </Stack>
                ) : (
                    <Stack position="absolute" bottom={'20%'} left={15}>
                        <TextWithIcon
                            icon={<GpsFixedOutlined sx={{ fontSize: '12', color: theme[themeStyle].true_white }} />}
                            text={audition?.shoot_location?.address ? audition?.shoot_location?.address : 'Remote'}
                            typographyProps={{
                                sx: {
                                    fontSize: 10,
                                    color: theme[themeStyle].true_white,
                                },
                            }}
                        />
                        <Typography
                            style={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 32,
                                color: theme[themeStyle].true_white,
                            }}>
                            {audition.title}
                        </Typography>
                        <CustomChipGroup
                            tags={audition.required_media}
                            tagSx={{
                                color: theme[themeStyle].true_white,
                            }}
                        />
                    </Stack>
                ))}
        </Box>
    );

    if (useImmersive) {
        return <Box sx={outerSx}>{inner}</Box>;
    }

    return <Card sx={outerSx}>{inner}</Card>;
};

export { CoverImageRenderer };
